import React, { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProviderSelector as AuthProvider } from '@/contexts/AuthProvider';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { HomeRoute } from './HomeRoute';
import InternalPage from '@/components/InternalPage';
import MortgageSidebarDemo from '@/components/ui/mortgage-sidebar';
import { PreApprovalBorrowerPortal } from '@/components/pre-approval/PreApprovalBorrowerPortal';
import User, { UserPreview } from '@/apps/loan-officer-profile/LoanOfficerProfile';
import TemplatePreview from '@/apps/template-preview/TemplatePreview';
import { Toaster } from '@/components/ui/sonner';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { SessionWarning } from '@/components/SessionWarning';
import { ChatPopout } from '@/components/chat';
import { QuickActionsButton } from '@/components/QuickActionsButton';
import { SessionService } from '@/services/sessionService';
import { OAuthCallback } from '@/components/OAuthCallback';
import { performanceTracker } from '@/utils/performanceTracker';
import { SecureUploadLanding } from '@/components/uploads/SecureUploadLanding';
import { RecruitVerification } from '@/components/uploads/RecruitVerification';
import { DocumentUploadPortal } from '@/components/uploads/DocumentUploadPortal';
import { LeadDocumentUploadPortal } from '@/components/uploads/LeadDocumentUploadPortal';
import { BorrowerSelfServiceVerification } from '@/components/uploads/BorrowerSelfServiceVerification';
import { BorrowerSelfServiceUpload } from '@/components/uploads/BorrowerSelfServiceUpload';
import { LoanDocumentsWrapper } from '@/components/encompass/prospects/LoanDocumentsWrapper';
import { ConditionsPortal } from '@/components/conditions-portal';
import { InternalConditionsPortal } from '@/components/conditions-portal/InternalConditionsPortal';
import { AuthRedirect } from '@/components/AuthRedirect';
import { BatCaveUploadPortal } from '@/components/batcave/BatCaveUploadPortal';
import { BatCaveAdmin } from '@/components/batcave/BatCaveAdmin';
import { ArtemisLoadingScreen } from '@/components/artemis/ArtemisLoadingScreen';
import { LoanCenterRedirect } from './LoanCenterRedirect';
import { InternalLightModeBoundary } from './InternalLightModeBoundary';
import { LegacyRateQuoteUnavailable } from '@/components/LegacyRateQuoteUnavailable';
import { isLegacyRateQuotePublicRoute } from '@/utils/routeUtils';

// Note: Console log capture is initialized in main.tsx (before all other imports)

const ArtemisPage = lazy(async () => {
  const module = await import('@/components/artemis/ArtemisPage');
  return { default: module.ArtemisPage };
});

const OnlineLeadsPage = lazy(async () => {
  const module = await import('@/components/artemis/online-leads/OnlineLeadsPage');
  return { default: module.OnlineLeadsPage };
});

const MarketingStudioPage = lazy(async () => {
  const module = await import('@/components/marketing/MarketingStudioPage');
  return { default: module.MarketingStudioPage };
});

const InvestorSlideOverParityHarness = import.meta.env.DEV
  ? lazy(async () => {
      const module = await import('@/components/investors/dev/InvestorSlideOverParityHarness');
      return { default: module.InvestorSlideOverParityHarness };
    })
  : null;

const UiQualityGallery = import.meta.env.DEV
  ? lazy(async () => {
      const module = await import('@/components/dev/UiQualityGallery');
      return { default: module.UiQualityGallery };
    })
  : null;

const DevRouteFallback: React.FC = () => (
  <div className="min-h-screen bg-slate-50 px-6 py-5 text-sm text-slate-600">
    Loading dev harness...
  </div>
);

interface InternalSectionRedirectProps {
  sectionId: string;
}

export const InternalSectionRedirect: React.FC<InternalSectionRedirectProps> = ({ sectionId }) => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  searchParams.set('section', sectionId);

  return (
    <Navigate
      to={{
        pathname: '/internal',
        search: `?${searchParams.toString()}`,
        hash: location.hash
      }}
      replace
    />
  );
};

// Load debug utilities only in development mode
// Skipped under vitest (DEV is true there too): these fire-and-forget loads
// can resolve after a test environment tears down, which vitest reports as an
// unhandled error and fails the run. The .catch keeps a failed lazy load from
// surfacing as an unhandled rejection in the browser as well.
if (import.meta.env.DEV && !import.meta.env.TEST) {
  import('@/utils/findAllUsers').catch(() => {}); // Load user finder
  import('@/utils/clearImpersonation').catch(() => {}); // Load impersonation clearer
  import('@/utils/diagnosticProfiles').catch(() => {}); // Load profile diagnostic tool
  import('@/utils/profileManagement').catch(() => {}); // Load profile management utilities
  import('@/utils/debugProfile404').catch(() => {}); // Load profile 404 debug utility
}

// HomeRoute lives in its own module (src/apps/main/HomeRoute.tsx) so the
// focused redirect integration test can import it without pulling in App's
// full dependency graph (PDFjs, services, etc.).

export const AppRoutes: React.FC = () => {
  useEffect(() => {
    // Mark first render
    performanceTracker.mark('first_render');

    // Initialize session monitoring
    SessionService.initialize();

    // Note: Console log capture is initialized at module level (top of file)

    // Suppress Chrome extension errors in production
    if (import.meta.env.PROD) {
      const originalError = console.error;
      console.error = (...args: unknown[]) => {
        // Filter out known Chrome extension noise
        const errorString = args[0]?.toString() || '';
        if (
          errorString.includes('message channel closed') ||
          errorString.includes('asynchronous response') ||
          errorString.includes('A listener indicated an asynchronous response')
        ) {
          return; // Silently ignore Chrome extension errors
        }
        originalError.apply(console, args);
      };
    }

    // Report performance metrics after a delay to ensure everything is loaded
    setTimeout(() => {
      performanceTracker.reportMetrics();
    }, 1000);
  }, []);

  return (
    <>
      <Routes>
        {/* Public routes - no auth required */}
        <Route path="/p/:slug" element={<User />} />
        <Route path="/pre-approval/:id/preview" element={<PreApprovalBorrowerPortal />} />
        <Route path="/pre-approval/:token" element={<PreApprovalBorrowerPortal />} />
        <Route path="/templates/preview/:templateId" element={<TemplatePreview />} />
        <Route path="/templates/demo/:templateId" element={<TemplatePreview />} />
        <Route path="/rate-quote/*" element={<LegacyRateQuoteUnavailable />} />

        {/* Retired Tools > Rate Quote bookmarks now land in Artemis. */}
        <Route path="/tools/rate-quote/*" element={<Navigate to="/internal/artemis" replace />} />
        <Route path="/internal/rate-quote/*" element={<Navigate to="/internal/artemis" replace />} />
        <Route path="/settings/rate-quote-template/*" element={<Navigate to="/internal/artemis" replace />} />

        {/* Secure document upload routes */}
        <Route path="/secureuploads" element={<SecureUploadLanding />} />
        <Route path="/secureuploads/recruit" element={<RecruitVerification />} />
        <Route path="/secureuploads/recruit/upload" element={<DocumentUploadPortal />} />
        {/* Borrower self-service upload (contact-based document vault) */}
        <Route path="/secureuploads/borrower" element={<BorrowerSelfServiceVerification />} />
        <Route path="/secureuploads/borrower/upload" element={<BorrowerSelfServiceUpload />} />
        {/* Legacy: Borrower upload via magic link (for backward compatibility) */}
        <Route path="/secureuploads/borrower/:token" element={<LeadDocumentUploadPortal />} />

        {/* Conditions Portal - public token-based access to loan conditions */}
        <Route path="/conditions/:token" element={<ConditionsPortal />} />

        {/* Bat Cave - secure document transfer for transitioning loan officers */}
        <Route path="/batcave" element={<BatCaveUploadPortal />} />

        {/* Singular aliases for secure upload routes */}
        <Route path="/secureupload" element={<Navigate to="/secureuploads" replace />} />
        <Route path="/secureupload/recruit" element={<Navigate to="/secureuploads/recruit" replace />} />
        <Route path="/secureupload/borrower/:token" element={<Navigate to="/secureuploads/borrower/:token" replace />} />
        <Route path="/secureupload/borrower" element={<Navigate to="/secureuploads/borrower" replace />} />

        {/* OAuth callback routes */}
        <Route path="/auth/callback" element={<OAuthCallback />} />
        <Route path="/auth/callback/cookie" element={<OAuthCallback />} />
        {/* Auth redirect - handles login redirects from email links */}
        <Route path="/auth/redirect" element={<AuthRedirect />} />

        {/* Friendly report URLs resolve to the permission-gated internal section. */}
        <Route
          path="/company-reports/pipeline-performance"
          element={<Navigate to="/internal?section=pipeline-performance-report" replace />}
        />
        <Route
          path="/company-reports/pickup"
          element={<InternalSectionRedirect sectionId="pickup-report" />}
        />
        <Route
          path="/company-reports/appraisal-delivery"
          element={<InternalSectionRedirect sectionId="appraisal-delivery-report" />}
        />
        <Route
          path="/internal/secondary/pickup-analysis"
          element={<InternalSectionRedirect sectionId="pickup-report" />}
        />
        <Route
          path="/internal/secondary/pickup-pipeline"
          element={<InternalSectionRedirect sectionId="pickup-pipeline" />}
        />

        {/* Auth-aware routes */}
        <Route path="/" element={<HomeRoute />} />
        <Route path="/loan-center" element={<LoanCenterRedirect />} />
        <Route path="/loan-data-exports" element={<Navigate to="/internal?section=loan-data-exports" replace />} />
        <Route
          path="/internal"
          element={
            <InternalLightModeBoundary>
              <ProtectedRoute>
                <InternalPage />
              </ProtectedRoute>
            </InternalLightModeBoundary>
          }
        />
        {/* URL-based document viewer - persists on page refresh */}
        <Route
          path="/internal/documents/:loanId"
          element={
            <InternalLightModeBoundary>
              <ProtectedRoute>
                <LoanDocumentsWrapper />
              </ProtectedRoute>
            </InternalLightModeBoundary>
          }
        />
        {/* Internal conditions portal - JWT-authenticated staff access */}
        <Route
          path="/internal/conditions/:loanId"
          element={
            <InternalLightModeBoundary>
              <ProtectedRoute>
                <InternalConditionsPortal />
              </ProtectedRoute>
            </InternalLightModeBoundary>
          }
        />
        {/* Artemis - standalone protected workspace */}
        <Route
          path="/internal/artemis"
          element={
            <ProtectedRoute loadingFallback={<ArtemisLoadingScreen status="Signing you in…" phase={1} />}>
              <Suspense fallback={<ArtemisLoadingScreen phase={2} />}>
                <ArtemisPage />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/internal/artemis/online-leads"
          element={
            <ProtectedRoute loadingFallback={<ArtemisLoadingScreen status="Signing you in…" phase={1} />}>
              <Suspense fallback={<ArtemisLoadingScreen phase={2} />}>
                <OnlineLeadsPage />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/marketing/*"
          element={
            <ProtectedRoute>
              <Suspense fallback={<DevRouteFallback />}>
                <MarketingStudioPage />
              </Suspense>
            </ProtectedRoute>
          }
        />
        {/* Booking bookmarks enter the same protected Studio membership boundary. */}
        <Route path="/book/marketing" element={<Navigate to="/marketing/book" replace />} />
        {/* Chat popout - standalone window for multi-monitor use */}
        <Route
          path="/chat-popout"
          element={
            <ProtectedRoute>
              <ChatPopout />
            </ProtectedRoute>
          }
        />
        {/* Bat Cave Admin - manage secure document transfer links */}
        <Route
          path="/batcave-admin"
          element={
            <ProtectedRoute>
              <BatCaveAdmin />
            </ProtectedRoute>
          }
        />
        <Route
          path="/sidebar-demo"
          element={
            <ProtectedRoute>
              <MortgageSidebarDemo />
            </ProtectedRoute>
          }
        />
        <Route
          path="/preview/p/:slug"
          element={
            <ProtectedRoute>
              <UserPreview />
            </ProtectedRoute>
          }
        />
        {import.meta.env.DEV && InvestorSlideOverParityHarness && (
          <Route
            path="/dev/investor-slideover-parity"
            element={
              <Suspense fallback={<DevRouteFallback />}>
                <InvestorSlideOverParityHarness />
              </Suspense>
            }
          />
        )}
        {import.meta.env.DEV && UiQualityGallery && (
          <Route
            path="/dev/ui-quality/*"
            element={
              <Suspense fallback={<DevRouteFallback />}>
                <UiQualityGallery />
              </Suspense>
            }
          />
        )}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
};

/**
 * Unified Quick Actions - Combines Chat and Feedback into a single FAB menu
 */
const ConditionalQuickActions: React.FC = () => {
  const location = useLocation();

  // Hide on public routes (secure uploads, profile pages, pre-approvals, conditions portal, bat cave)
  const shouldHide =
    location.pathname.startsWith('/secureuploads') ||
    location.pathname.startsWith('/secureupload') ||
    location.pathname.startsWith('/p/') ||
    location.pathname.startsWith('/pre-approval/') ||
    location.pathname.startsWith('/conditions/') ||
    isLegacyRateQuotePublicRoute(location.pathname) ||
    location.pathname.startsWith('/internal/conditions/') ||
    location.pathname.startsWith('/internal/artemis') ||
    location.pathname.startsWith('/marketing') ||
    location.pathname === '/book/marketing' ||
    location.pathname === '/batcave' ||
    location.pathname.startsWith('/chat-popout');

  if (shouldHide) {
    return null;
  }

  const handleOpenChat = () => {
    // Open chat in a popout window for multi-monitor support
    const width = 500;
    const height = 700;
    const left = window.screenX + window.outerWidth - width - 50;
    const top = window.screenY + 50;

    window.open(
      '/chat-popout',
      'lendwise-chat',
      `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`
    );
  };

  return (
    <QuickActionsButton
      isChatOpen={false}
      onOpenChat={handleOpenChat}
    />
  );
};

/**
 * Routes that render WITHOUT AuthProvider. This prevents iframe auth conflicts
 * for previews and keeps terminal borrower links outside employee-session UI.
 */
const IsolatedRoutes: React.FC = () => {
  return (
    <Routes>
      <Route path="/templates/preview/:templateId" element={<TemplatePreview />} />
      <Route path="/templates/demo/:templateId" element={<TemplatePreview />} />
      <Route path="/conditions/:token" element={<ConditionsPortal />} />
      <Route path="/rate-quote/*" element={<LegacyRateQuoteUnavailable />} />
      {import.meta.env.DEV && UiQualityGallery && (
        <Route
          path="/dev/ui-quality/*"
          element={
            <Suspense fallback={<DevRouteFallback />}>
              <UiQualityGallery />
            </Suspense>
          }
        />
      )}
    </Routes>
  );
};

/**
 * Check if current path should be rendered without AuthProvider.
 */
// eslint-disable-next-line react-refresh/only-export-components -- route ownership is a pure contract covered by focused tests
export const isIsolatedMainRoute = (path: string): boolean => {
  return (
    path.startsWith('/templates/preview/') ||
    path.startsWith('/templates/demo/') ||
    path.startsWith('/conditions/') ||
    isLegacyRateQuotePublicRoute(path) ||
    (import.meta.env.DEV && (path === '/dev/ui-quality' || path.startsWith('/dev/ui-quality/')))
  );
};

const shouldRenderIsolated = () => {
  const path = window.location.pathname;
  return isIsolatedMainRoute(path);
};

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter basename="/" future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        {shouldRenderIsolated() ? (
          // Render isolated routes (templates, conditions portal) WITHOUT AuthProvider
          <div className="h-screen w-full overflow-y-auto bg-background font-sans antialiased">
            <IsolatedRoutes />
            <Toaster />
          </div>
        ) : (
          // Normal app with full auth
          <AuthProvider>
            <div className="min-h-screen w-full bg-background font-sans antialiased">
              <AppRoutes />
              <SessionWarning />
              <Toaster />
              <ConditionalQuickActions />
            </div>
          </AuthProvider>
        )}
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
